import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { LoginDetails } from "../../reduxToolkit/slice/auth";
import { baseURL } from "../../config/config";
import Swal from "sweetalert2";

const AdminNavbar = (props) => {
  const { name, profileImage } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [profileImage]);
  const dropdownRef = useRef(null);

  // Get page title from current route
  const getPageTitle = () => {
    const path = location.pathname;
    // Most specific paths first
    const titles = [
      ['/admin/index', 'Dashboard'],
      ['/admin/change-password', 'Company Password'],
      ['/admin/user', 'Companies'],
      ['/admin/sale-category', 'Sale Categories'],
      ['/admin/sale-type', 'Sale Types'],
      ['/admin/sub-category', 'Categories'],
      ['/admin/category', 'Categories'],
      ['/admin/default-template', 'Default Templates'],
      ['/admin/template', 'Default Templates'],
      ['/admin/plan-features', 'Plan Features'],
      ['/admin/pricing', 'Plans'],
      ['/admin/email-template', 'Company Email Templates'],
      ['/admin/email', 'Email Templates'],
      ['/admin/profile', 'Profile'],
    ];
    const match = titles.find(([prefix]) => path.startsWith(prefix));
    return match ? match[1] : 'Dashboard';
  };

  const logOutHandler = () => {
    setDropdownOpen(false);
    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to sign out?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#AD2429',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Sign Out',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(LoginDetails({ token: null, name: null, userId: null, email: null, isLogin: false }));
        Navigate("/auth/login");
      }
    });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'AD';

  return (
    <div style={{
      height: 64,
      background: '#fff',
      borderBottom: '1px solid #edf0f7',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 28px',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      width: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Page Title */}
      <div>
        <h1 style={{
          fontSize: 18,
          fontWeight: 700,
          color: '#0a1628',
          margin: 0,
          letterSpacing: '-0.3px',
        }}>
          {getPageTitle()}
        </h1>
        <div style={{ fontSize: 12, color: '#9aa5c0', marginTop: 1 }}>
          Super Admin Portal
        </div>
      </div>

      {/* Right side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>

        {/* Avatar + Dropdown */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'none',
              border: '1px solid #edf0f7',
              borderRadius: 10,
              padding: '6px 12px 6px 6px',
              cursor: 'pointer',
              transition: 'background .15s',
            }}
          >
            {/* Avatar */}
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: profileImage && !avatarFailed ? 'transparent' : '#AD2429',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}>
              {profileImage && !avatarFailed
                ? <img src={`${baseURL}/${profileImage}`} alt="" onError={() => setAvatarFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>{initials}</span>
              }
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#0a1628', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
              {name || 'Admin'}
            </span>
            <span style={{ fontSize: 10, color: '#9aa5c0', marginLeft: 2 }}>▾</span>
          </button>

          {/* Dropdown */}
          {dropdownOpen && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 8px)',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(26,39,68,.12), 0 0 0 1px rgba(26,39,68,.06)',
              minWidth: 180,
              maxWidth: 240,
              overflow: 'hidden',
              zIndex: 100,
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f2f8' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'Admin'}</div>
                <div style={{ fontSize: 12, color: '#9aa5c0', marginTop: 2 }}>Super Administrator</div>
              </div>
              <Link
                to="/admin/profile"
                onClick={() => setDropdownOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '11px 16px',
                  fontSize: 14,
                  color: '#3a4a6a',
                  textDecoration: 'none',
                  transition: 'background .1s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8f9fc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span>👤</span> My Profile
              </Link>
              <button
                onClick={logOutHandler}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '11px 16px',
                  fontSize: 14,
                  color: '#AD2429',
                  background: 'none',
                  border: 'none',
                  width: '100%',
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderTop: '1px solid #f0f2f8',
                  fontFamily: 'inherit',
                  transition: 'background .1s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span>🚪</span> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNavbar;
